import { useRef } from "react";
import { Html } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { useTranslation } from "react-i18next";
import { anchorOnAisle, coverageBand, heatBucket, type AisleBadge } from "./kpiBadge";
import { LABEL_Z_RANGE } from "./labelZ";
import styles from "./KpiBadge.module.css";

interface Props {
  badges: AisleBadge[];
  showCoverage: boolean;
  showOccupancy: boolean;
  showVertical: boolean;
  showAccuracy: boolean;
}

const BAND_CLASS = { good: "good", warn: "warn", bad: "bad", na: "na" } as const;

/** Clearance above the aisle's tallest rack — enough to read the card without
 *  it floating free of the aisle it belongs to. */
const BADGE_LIFT_M = 1.2;

/** Reused across frames: this runs for every badge, every frame. */
const _viewDir = new THREE.Vector3();

/** Integer percent for a badge — small enough that decimals (and the pt-BR
 *  decimal comma) would only add noise at billboard scale. */
const pct = (v: number | null) => (v === null ? "—" : `${Math.round(v)}%`);

/**
 * ADR 193: per-aisle KPI billboards. DOM (`<Html>`, the forklift-label
 * pattern) rather than geometry text: crisp at any zoom and styled straight
 * from the design tokens. One badge per aisle, floating above its tallest
 * rack; each KPI chip mounts only when its layer toggle is on.
 *
 * ADR 207: a vertical card — aisle name on top, then one labelled row per KPI
 * (Cobertura / Ocupação / Vertical / Acuracidade), the value coloured by its
 * band. The former single-line pill packed three bare percentages too small to
 * read.
 *
 * ADR 292: the vertical row carries its sample count inline (`64 % ·441`). It
 * is a mean over the bins the rack-height sensor happened to measure — roughly
 * a quarter of them — and a bare percentage next to the slot occupancy would
 * read as the same kind of complete measurement, which it is not.
 */
export function KpiBadgeLayer({ badges, showCoverage, showOccupancy, showVertical, showAccuracy }: Props) {
  const { t } = useTranslation();
  const heat = (v: number | null) => heatBucket(v);

  // ADR 214: each badge rides a group whose position is re-derived per frame,
  // so the card slides along its own aisle to meet the camera. Imperative on
  // purpose — a per-frame React state update for 13 billboards would re-render
  // the whole layer 60×/s.
  const groups = useRef<(THREE.Group | null)[]>([]);
  useFrame(({ camera }) => {
    camera.getWorldDirection(_viewDir);
    const dir: [number, number, number] = [_viewDir.x, _viewDir.y, _viewDir.z];
    const pos: [number, number, number] = [camera.position.x, camera.position.y, camera.position.z];
    badges.forEach((b, i) => {
      const g = groups.current[i];
      if (!g) return;
      const [x, y, z] = anchorOnAisle(b, pos, dir, b.topM + BADGE_LIFT_M);
      g.position.set(x, y, z);
    });
  });

  return (
    <>
      {badges.map((b, i) => (
        <group
          key={b.key}
          ref={(g) => { groups.current[i] = g; }}
          position={[b.x, b.topM + BADGE_LIFT_M, b.y]}
        >
        <Html
          center
          distanceFactor={30}
          occlude={false}
          // Capped under the /live panels — a label must never cover the chrome.
          zIndexRange={LABEL_Z_RANGE}
        >
          <div className={styles.badge}>
            <span className={styles.aisle}>{b.aisle}</span>
            {showCoverage && (
              <div className={styles.row}>
                <span className={styles.rowLabel}>{t("kpi.badge.coverage")}</span>
                <span className={`${styles.rowValue} ${styles[BAND_CLASS[coverageBand(b.coveragePct)]]}`}>
                  {pct(b.coveragePct)}
                </span>
              </div>
            )}
            {showOccupancy && (
              <div className={styles.row}>
                <span className={styles.rowLabel}>{t("kpi.badge.occupancy")}</span>
                <span className={styles.rowValue}>
                  <i
                    className={styles.dot}
                    style={heat(b.occupancyPct) !== null ? { background: `var(--color-heat-${heat(b.occupancyPct)})` } : undefined}
                  />
                  {pct(b.occupancyPct)}
                </span>
              </div>
            )}
            {/* ADR 292: vertical fill of the measured occupied bins. Neutral like
                occupancy — a low pallet is not a fault, it is head-room. */}
            {showVertical && (
              <div className={styles.row}>
                <span className={styles.rowLabel}>{t("kpi.badge.vertical")}</span>
                <span className={styles.rowValue}>
                  {/* ADR 293: "≈" where the denominator is a clear height the twin states
                      rather than one the kit measured — the value is a good estimate, not
                      an instrument reading, and the badge has room for exactly one glyph
                      to say so. The dock spells it out. */}
                  {b.verticalDerived && b.verticalFillPct !== null ? "≈" : ""}
                  {pct(b.verticalFillPct)}
                  {b.verticalMeasuredSpaces > 0 && (
                    <span className={styles.sample}>·{b.verticalMeasuredSpaces}</span>
                  )}
                </span>
              </div>
            )}
            {/* ADR 195: WMS agreement — a quality measure like coverage, so it
                shares the same semantic bands. "—" = no WMS snapshot imported. */}
            {showAccuracy && (
              <div className={styles.row}>
                <span className={styles.rowLabel}>{t("kpi.badge.accuracy")}</span>
                <span className={`${styles.rowValue} ${styles[BAND_CLASS[coverageBand(b.agreementPct)]]}`}>
                  {pct(b.agreementPct)}
                </span>
              </div>
            )}
          </div>
        </Html>
        </group>
      ))}
    </>
  );
}
